import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon, InfoIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import useDialogAlert from '@/hooks/useDialogAlert';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import { selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { getFormErrorsKey, getWebviewsUrl } from '@/util/util';
import { surveyActionSchema } from '@/util/validation/schemas/action-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

const satisfactionSurveyUrl = `${getWebviewsUrl()}/common/survey`;
const customSurveyExampleUrl = 'https://www.example.com/survey/placeholder';

const SURVEY_OPTIONS = { SATISFACTION_SURVEY: 'Satisfaction survey', CUSTOM: 'custom' } as const;

type SurveyAction = Extract<Action, { type: 'survey' }>;

type SurveyForm = {
  survey_type: (typeof SURVEY_OPTIONS)[keyof typeof SURVEY_OPTIONS];
  url: string;
  name: string;
  label: string;
  height: string;
};

const getFormDefaults = (survey: Omit<SurveyAction, 'action_id'>): SurveyForm => ({
  survey_type:
    survey.url === satisfactionSurveyUrl
      ? SURVEY_OPTIONS.SATISFACTION_SURVEY
      : SURVEY_OPTIONS.CUSTOM,
  name: survey.name,
  label: survey.label,
  url: survey.url,
  height: survey.height,
});

const ToolkitActionSurvey = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const typeOptions = useMemo(
    () => [
      { value: SURVEY_OPTIONS.SATISFACTION_SURVEY, label: t('dialog.survey.satisfaction_survey') },
      { value: SURVEY_OPTIONS.CUSTOM, label: t('dialog.survey.custom') },
    ],
    [t]
  );

  const heightOptions = useMemo(
    () => [
      { value: 'tall', label: t('dialog.webview.tall') },
      { value: 'compact', label: t('dialog.webview.compact') },
      { value: 'full', label: t('dialog.webview.full') },
      { value: 'new_window', label: t('dialog.webview.new_window') },
    ],
    [t]
  );

  const { actionId, ...survey } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as SurveyAction;
    const { action_id, ...rest } = selectedAction;
    return { actionId: action_id, ...rest };
  }, shallowEqual);

  const form = useForm<SurveyForm>({
    mode: 'onChange',
    defaultValues: getFormDefaults(survey),
    resolver: yupResolver(surveyActionSchema) as Resolver<SurveyForm>,
  });
  const {
    control,
    reset,
    trigger,
    setFocus,
    setValue,
    getValues,
    formState: { errors },
  } = form;
  const watchSurveyType = useWatch({ control, name: 'survey_type' });

  // Reset form when switching to a different action
  useEffect(() => {
    reset(getFormDefaults(survey));
  }, [actionId, reset]);

  const handleFieldChange = useCallback(
    (key: keyof SurveyForm, value: string) => {
      dispatch(updateAction({ actionId, action: { [key]: value } }));
    },
    [actionId, dispatch]
  );

  const handleSurveyTypeChange = useCallback(() => {
    const surveyType = getValues('survey_type');
    const url =
      surveyType === SURVEY_OPTIONS.CUSTOM ? customSurveyExampleUrl : satisfactionSurveyUrl;

    setValue('url', url);
    handleFieldChange('url', url);
    trigger('url');
  }, [getValues, handleFieldChange, trigger, setValue]);

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: survey.node_id,
    title: t('actions.types.survey'),
    type: survey.type,
  });

  useEffect(() => {
    const fields = ['url', 'name', 'label'] as const;

    for (const field of fields) {
      setFieldError(field, errors[field]?.message);
    }
  }, [getFormErrorsKey(errors), setFieldError]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  useFocusOnInput('survey_type', errors, setFocus);

  return (
    <ToolkitWrapper type="survey">
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6">
          <FormField
            control={control}
            name="survey_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.survey.survey_type')}</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleSurveyTypeChange();
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {typeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          {watchSurveyType === SURVEY_OPTIONS.CUSTOM && (
            <FormField
              control={control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('dialog.survey.survey_url')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      minRows={1}
                      maxRows={4}
                      className="resize-none break-all"
                      placeholder={t('dialog.url_placeholder')}
                      onChange={(event) => {
                        field.onChange(event);
                        handleFieldChange('url', event.target.value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('common.title')}
                  <InfoTooltip content={t('dialog.survey.title_tooltip')} side="right">
                    <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                  </InfoTooltip>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={t('dialog.webview.type_title')}
                    onChange={(event) => {
                      field.onChange(event);
                      handleFieldChange('name', event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.webview.button_label')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={t('dialog.webview.label_placeholder')}
                    onChange={(event) => {
                      field.onChange(event);
                      handleFieldChange('label', event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="height"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.survey.format')}
                  <InfoTooltip content={t('dialog.survey.format_tooltip')} side="right">
                    <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                  </InfoTooltip>
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleFieldChange('height', value);
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {heightOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <Alert variant="info" className="gap-y-2">
            <InfoIcon size={16} />
            <AlertTitle className="text-sm leading-5 font-semibold tracking-normal">
              {t('dialog.survey.format_option')}
            </AlertTitle>
            <AlertDescription>{t('dialog.survey.format_option_text')}</AlertDescription>
          </Alert>
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionSurvey;
